import { useCallback } from 'react';
import { useMerchantCreation } from './useMerchantCreation';
import { OptionBase } from '../../../../../../components/Select/types';

type UseMerchantFieldParams = {
  onMerchantChange: (merchantId: string) => void;
  updateExpenseWithMerchant: (merchantId: string) => Promise<void>;
  setMerchantOptions: React.Dispatch<React.SetStateAction<OptionBase[]>>;
  merchantOptions: OptionBase[];
};

type UseMerchantFieldReturn = {
  handleCreateMerchant: (merchantName: string) => Promise<void>;
  isCreatingMerchant: boolean;
};

export const useMerchantField = ({
  onMerchantChange,
  updateExpenseWithMerchant,
  setMerchantOptions,
  merchantOptions,
}: UseMerchantFieldParams): UseMerchantFieldReturn => {
  const { createMerchant, isCreatingMerchant } = useMerchantCreation();

  const handleCreateMerchant = useCallback(
    async (merchantName: string) => {
      const newMerchant = await createMerchant(merchantName);

      if (newMerchant) {
        const newOption: OptionBase = {
          label: newMerchant.name,
          value: newMerchant.id,
        };

        setMerchantOptions((prev) => {
          const exists = prev.some(
            (m) => m.value === newMerchant.id || m.label === newMerchant.name,
          );
          if (exists) {
            return prev;
          }
          return [...prev, newOption];
        });

        onMerchantChange(newMerchant.id);
        await updateExpenseWithMerchant(newMerchant.id);
      }
    },
    [
      createMerchant,
      onMerchantChange,
      updateExpenseWithMerchant,
      setMerchantOptions,
    ],
  );

  return {
    handleCreateMerchant,
    isCreatingMerchant,
  };
};
